import {
  Col,
  Row,
  Space,
  notification,
  Dropdown,
  Menu,
  Pagination,
} from 'antd';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/helpers';
import MobHeader from '../../Components/Header/MobHeader';
import {
  StudyStatusTag,
  mapStudyStatusToDisplay,
} from '../../Components/StatusTag';
import {
  ThemeFilterBtn,
  ThemeButton,
  ThemeTableCard,
  ThemeTable,
  ThemeInput,
  ThemeMainWrapper,
  ThemeModal,
  CardPaginationWrapper,
} from '../Styles/Theme';
import {
  CampaignNameLink,
  CampaignCard,
  CampaignCardInner,
  CampaignCardHeader,
  CampaignCardTitle,
  CampaignCardSubtitle,
  CampaignCardFooter,
  CampaignCardDate,
  TableActionWrapper,
  CardMenuIcon,
} from './CampaignsStyle';
import { ThemeProjectTabWrapper } from '../Projects/ProjectsStyle';
import Squares from '../Dashboard/Squares';
import { GreetingText } from '../Dashboard/DashboardStyle';
import { SearchOutlined, LinkOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import {
  getCampaignsApi,
  getCampaignsReset,
  deleteCampaignApi,
  deleteCampaignReset,
} from '../../store/actions/campaignAction';
import { useCampaignPermissions } from '../../utils/permissions';
import useFolders from "../../hooks/useFolders";
import {
  FolderBackNav,
  FolderCardsSection,
  ItemsSectionLabel,
  NewFolderButton,
  FolderModals,
} from "../../Components/FolderComponents";

const Campaigns = props => {
  const {
    getCampaigns,
    getCampaignsReset,
    getCampaignsState,
    deleteCampaign,
    deleteCampaignReset,
    deleteCampaignState,
  } = props;

  const navigate = useNavigate();

  // Permission check - shortcut hook, no need to remember module name
  const { can } = useCampaignPermissions();

  const [activeView, setActiveView] = useState('card');
  const [tableData, setTableData] = useState([]);
  const [cardData, setCardData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);

  const defaultFilters = {
    page: 1,
    limit: 12,
    search: '',
    status: '',
    sortBy: 'createdAt',
    sortDirection: 'desc',
  };

  const getFiltersFromUrl = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlFilters = {};
    for (const [key, value] of urlParams.entries()) {
      urlFilters[key] = value;
    }
    const filters = {
      ...defaultFilters,
      ...urlFilters,
    };
    return filters;
  };

  const initialFilters = getFiltersFromUrl();
  const [filters, setFilters] = useState(initialFilters);
  const [searchValue, setSearchValue] = useState(initialFilters.search || '');

  // Shared folder hook
  const folderHook = useFolders({ entityType: 'campaign', searchValue });
  const {
    folders,
    filteredFolders,
    currentFolderId,
    currentFolderName,
    refreshNeeded,
    createFolderModal,
    setCreateFolderModal,
    renameFolderModal,
    setRenameFolderModal,
    folderToRename,
    deleteFolderModal,
    setDeleteFolderModal,
    folderToDelete,
    moveToFolderModal,
    setMoveToFolderModal,
    selectedFolderId,
    setSelectedFolderId,
    removeFromFolderModal,
    setRemoveFromFolderModal,
    itemToRemove: campaignToRemove,
    foldersCollapsed,
    setFoldersCollapsed,
    itemsCollapsed: studiesCollapsed,
    setItemsCollapsed: setStudiesCollapsed,
    draggedItem: draggedCampaign,
    dragOverFolderId,
    folderForm,
    renameFolderForm,
    createFolderState,
    renameFolderState,
    deleteFolderState,
    addItemToFolderState,
    removeItemFromFolderState,
    fetchFolders,
    getFoldersState,
    handleGetFoldersResponse,
    handleCreateFolderResponse,
    handleRenameFolderResponse,
    handleDeleteFolderResponse,
    handleAddItemToFolderResponse,
    handleRemoveItemFromFolderResponse,
    handleCreateFolder,
    handleRenameFolder,
    handleDeleteFolder,
    handleMoveToFolder,
    handleRemoveFromFolder,
    confirmRemoveFromFolder,
    handleFolderClick,
    handleBackToRoot,
    openRenameFolderModal,
    openDeleteFolderModal,
    openMoveToFolderModal,
    handleDragStart,
    handleDragEnd,
    handleFolderDragOver,
    handleFolderDragEnter,
    handleFolderDragLeave,
    handleFolderDrop,
  } = folderHook;

  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    return () => {
      window.removeEventListener('resize', checkIsMobile);
    };
  }, []);

  // Handle URL-based pagination and filters
  useEffect(() => {
    const updatedFilters = getFiltersFromUrl();
    setFilters({ ...updatedFilters });
    setSearchValue(updatedFilters.search || '');
    if (currentFolderId) {
      getCampaigns({ ...updatedFilters, folderId: currentFolderId });
    } else {
      getCampaigns({ ...updatedFilters, excludeFoldered: true });
    }
  }, [window.location.search, currentFolderId]);

  // Handle campaigns response
  useEffect(() => {
    if (getCampaignsState.apiState === 'success' && getCampaignsState.data) {
      const campaignsData =
        getCampaignsState.data.data || getCampaignsState.data;
      const campaigns = campaignsData.campaigns || [];

      if (campaigns && campaigns.length > 0) {
        setTotalRecords(
          campaignsData.pagination?.total || campaignsData.total_count || 0
        );

        const updatedCampaigns = campaigns.map(item => {
          return {
            key: item._id,
            id: item._id,
            name: item.campaign_name || '-',
            campaignName: item.campaign_name || '-',
            agentName:
              item.polling_agent?.agent_name || item.polling_agent?.name || '-',
            agentId: item.polling_agent?.agent_id || '-',
            createdOn: formatDate(item.createdAt),
            status: mapStudyStatusToDisplay(item.status || 'saved'),
            campaignStatus: item.status || 'saved',
            campaignLink: item.campaign_link || null,
          };
        });

        setTableData(updatedCampaigns);
        setCardData(updatedCampaigns);
      } else {
        setTableData([]);
        setCardData([]);
        setTotalRecords(0);
      }
      getCampaignsReset();
    } else if (getCampaignsState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: getCampaignsState.message || 'Failed to fetch campaigns',
      });
      getCampaignsReset();
    }
  }, [getCampaignsState, getCampaignsReset]);

  // Handle delete response
  useEffect(() => {
    if (deleteCampaignState.apiState === 'success') {
      notification.success({
        message: 'Success',
        description:
          deleteCampaignState.message || 'Study deleted successfully',
      });
      setDeleteModalVisible(false);
      setSelectedCampaign(null);
      refreshCampaigns();
    } else if (deleteCampaignState.apiState === 'error') {
      notification.error({
        message: 'Error',
        description: deleteCampaignState.message || 'Failed to delete campaign',
      });
    }
    deleteCampaignReset();
  }, [deleteCampaignState, deleteCampaignReset, getCampaigns]);

  // Helper to re-fetch campaigns with current folder context
  const refreshCampaigns = () => {
    const updatedFilters = getFiltersFromUrl();
    if (currentFolderId) {
      getCampaigns({ ...updatedFilters, folderId: currentFolderId });
    } else {
      getCampaigns({ ...updatedFilters, excludeFoldered: true });
    }
  };

  // Folder hook: fetch on mount
  useEffect(() => {
    fetchFolders();
  }, []);

  // Folder hook: response handlers
  useEffect(() => { handleGetFoldersResponse(); }, [getFoldersState]);
  useEffect(() => { handleCreateFolderResponse(); }, [createFolderState]);
  useEffect(() => { handleRenameFolderResponse(); }, [renameFolderState]);
  useEffect(() => { handleDeleteFolderResponse(); }, [deleteFolderState]);
  useEffect(() => { handleAddItemToFolderResponse(); }, [addItemToFolderState]);
  useEffect(() => { handleRemoveItemFromFolderResponse(); }, [removeItemFromFolderState]);

  // Re-fetch campaigns when folder hook signals a refresh (after folder operations)
  useEffect(() => {
    if (refreshNeeded > 0) {
      refreshCampaigns();
    }
  }, [refreshNeeded]);

  // Build campaign detail URL with folder context for back navigation
  const getCampaignDetailUrl = (campaignId) => {
    if (currentFolderId) {
      return `/campaigns/${campaignId}?fromFolderId=${currentFolderId}&fromFolderName=${encodeURIComponent(currentFolderName)}`;
    }
    return `/campaigns/${campaignId}`;
  };

  // Build create campaign URL with folder context (so new campaign gets added to folder)
  const getCreateCampaignUrl = () => {
    if (currentFolderId) {
      return `/campaigns/create-campaign?folderId=${currentFolderId}&folderName=${encodeURIComponent(currentFolderName)}`;
    }
    return '/campaigns/create-campaign';
  };

  const handleSearch = value => {
    const queryParams = new URLSearchParams(window.location.search);
    if (value) {
      queryParams.set('search', value);
    } else {
      queryParams.delete('search');
    }
    queryParams.set('page', '1');
    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    window.history.pushState({}, '', newUrl);
    setFilters({ ...filters, search: value, page: 1 });
    setSearchValue(value);
  };

  const handleTableChange = (pagination, _, sorter) => {
    const queryParams = new URLSearchParams(window.location.search);
    queryParams.set('page', pagination.current || 1);
    queryParams.set('limit', pagination.pageSize || 12);

    if (sorter && Object.keys(sorter).length > 0) {
      queryParams.set('sortBy', sorter.field || 'createdAt');
      queryParams.set(
        'sortDirection',
        sorter.order === 'ascend' ? 'asc' : 'desc'
      );
    } else {
      queryParams.set('sortBy', 'createdAt');
      queryParams.set('sortDirection', 'desc');
    }

    const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
    window.history.pushState({}, '', newUrl);
    setFilters({
      ...filters,
      sortBy: sorter?.field || 'createdAt',
      sortDirection: sorter?.order === 'ascend' ? 'asc' : 'desc',
      page: pagination.current || 1,
      limit: pagination.pageSize || 12,
    });
  };

  const handleEdit = record => {
    let editUrl = `/campaigns/create-campaign/${record.id}`;
    if (currentFolderId) {
      editUrl += `?folderId=${currentFolderId}&folderName=${encodeURIComponent(currentFolderName)}`;
    }
    navigate(editUrl);
  };

  const handleDelete = record => {
    setSelectedCampaign(record);
    setDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    if (selectedCampaign) {
      deleteCampaign(selectedCampaign.id);
    }
  };

  // Render card menu following reference codebase pattern (PollAgents.js)
  const renderCardMenu = record => {
    return (
      <Menu
        selectable={false}
        onClick={({ key, domEvent }) => {
          domEvent.stopPropagation();
          if (key === 'view') {
            navigate(getCampaignDetailUrl(record.id));
          } else if (key === 'edit') {
            handleEdit(record);
          } else if (key === 'moveToFolder') {
            openMoveToFolderModal(record);
          } else if (key === 'removeFromFolder') {
            handleRemoveFromFolder(record);
          } else if (key === 'delete') {
            handleDelete(record);
          } else if (key === 'link') {
            const fullUrl = `${window.location.origin}${record.campaignLink}`;
            window.open(fullUrl, '_blank', 'noopener,noreferrer');
          }
        }}
      >
        <Menu.Item key="view">View Details</Menu.Item>
        {can('edit') &&
          record.campaignStatus !== 'completed' &&
          record.campaignStatus !== 'closed' && (
            <Menu.Item key="edit">Edit</Menu.Item>
          )}
        {record.campaignLink && <Menu.Item key="link">Open Link</Menu.Item>}
        <Menu.Item key="moveToFolder">Move to Folder</Menu.Item>
        {currentFolderId && (
          <Menu.Item key="removeFromFolder">Remove from Folder</Menu.Item>
        )}
        {can('delete') && (
          <>
            <Menu.Divider />
            <Menu.Item key="delete">Delete</Menu.Item>
          </>
        )}
      </Menu>
    );
  };

  const columns = [
    {
      title: 'Study Name',
      dataIndex: 'campaignName',
      key: 'campaignName',
      render: (text, record) => (
        <CampaignNameLink onClick={() => navigate(getCampaignDetailUrl(record.id))}>
          {text}
        </CampaignNameLink>
      ),
    },
    {
      title: 'Agent',
      dataIndex: 'agentName',
      key: 'agentName',
      render: text => <span>{text}</span>,
    },
    {
      title: 'Created On',
      dataIndex: 'createdOn',
      key: 'createdOn',
      width: 130,
      sorter: true,
      render: text => <span>{text}</span>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: status => {
        return <StudyStatusTag status={status} />;
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      align: 'center',
      width: 180,
      render: (_, record) => (
        <TableActionWrapper>
          {can('edit') &&
            record.campaignStatus !== 'completed' &&
            record.campaignStatus !== 'closed' && (
              <ThemeFilterBtn
                shape="circle"
                size="medium"
                icon={<img src="/images/edit-icon.svg" alt="Edit" />}
                onClick={() => handleEdit(record)}
              />
            )}
          <Dropdown
            trigger={['click']}
            placement="bottomRight"
            dropdownRender={() => renderCardMenu(record)}
          >
            <ThemeFilterBtn
              shape="circle"
              size="medium"
              icon={<img src="/images/menu-dots-vertical.svg" alt="More" />}
              onClick={(e) => e.stopPropagation()}
            />
          </Dropdown>
        </TableActionWrapper>
      ),
    },
  ];

  return (
    <>
      <MobHeader />
      <ThemeMainWrapper>
        <Row gutter={[16, 16]} className="mb-6" align="middle">
          <Col
            xs={{ span: 8, order: 1 }}
            sm={{ span: 8, order: 1 }}
            md={{ span: 6, order: 1 }}
            lg={6}
            xl={8}
          >
            <GreetingText>Launch</GreetingText>
          </Col>
          <Col
            xs={{ span: 24, order: 3 }}
            sm={{ span: 24, order: 3 }}
            md={{ span: 8, order: 1 }}
            lg={8}
            xl={8}
            className="text-center sm:text-center md:text-end lg:text-center xl:text-center"
          >
            <ThemeProjectTabWrapper>
              <div className="view-toggle">
                <button
                  className={`toggle-btn ${
                    activeView === 'card' ? 'active' : ''
                  }`}
                  onClick={() => setActiveView('card')}
                >
                  Card View
                </button>
                <button
                  className={`toggle-btn ${
                    activeView === 'table' ? 'active' : ''
                  }`}
                  onClick={() => setActiveView('table')}
                >
                  Table View
                </button>
              </div>
            </ThemeProjectTabWrapper>
          </Col>
          <Col
            xs={{ span: 16, order: 2 }}
            sm={{ span: 16, order: 2 }}
            md={{ span: 10, order: 1 }}
            lg={10}
            xl={8}
            className="text-end"
          >
            <Space gap={10}>
              {can('create') &&
                (isMobile ? (
                  <>
                    {!currentFolderId && (
                      <ThemeFilterBtn
                        className="background"
                        size="large"
                        shape="square"
                        icon={<img src="/images/folder-open-purple.svg" alt="Folder" style={{ width: 18, height: 18 }} />}
                        onClick={() => setCreateFolderModal(true)}
                      />
                    )}
                    <Link to={getCreateCampaignUrl()}>
                      <ThemeFilterBtn
                        className="background"
                        size="large"
                        shape="square"
                        icon={<img src="/images/plus-icon.svg" alt="New Study" />}
                      />
                    </Link>
                  </>
                ) : (
                  <>
                    {!currentFolderId && (
                      <NewFolderButton isMobile={false} onClick={() => setCreateFolderModal(true)} />
                    )}
                    <Link to={getCreateCampaignUrl()}>
                      <ThemeButton
                        size="large"
                        icon={<img src="/images/plus-icon.svg" alt="New Study" />}
                      >
                        New Study
                      </ThemeButton>
                    </Link>
                  </>
                ))}
            </Space>
          </Col>
        </Row>
        {currentFolderId && (
          <FolderBackNav currentFolderName={currentFolderName} onBack={handleBackToRoot} />
        )}
        <Row gutter={[16, 16]} className="mb-6" align="middle">
          <Col xs={24} sm={12} md={12} lg={10} xl={6} xxl={4}>
            <ThemeInput
              className="shadow"
              placeholder="Search studies..."
              size="large"
              shape="square"
              prefix={<SearchOutlined />}
              value={searchValue}
              onChange={e => {
                const value = e.target.value;
                setSearchValue(value);
                if (!value) {
                  handleSearch('');
                }
              }}
              onPressEnter={e => handleSearch(e.target.value)}
            />
          </Col>
        </Row>
        {activeView === 'card' && (
          <>
            {/* Folder section (only at root, not inside a folder) */}
            {!currentFolderId && (
              <>
                <FolderCardsSection
                  filteredFolders={filteredFolders}
                  foldersCollapsed={foldersCollapsed}
                  setFoldersCollapsed={setFoldersCollapsed}
                  onFolderClick={handleFolderClick}
                  onRenameFolderClick={openRenameFolderModal}
                  onDeleteFolderClick={openDeleteFolderModal}
                  enableDrag={true}
                  dragOverFolderId={dragOverFolderId}
                  onDragOver={handleFolderDragOver}
                  onDragEnter={handleFolderDragEnter}
                  onDragLeave={handleFolderDragLeave}
                  onDrop={handleFolderDrop}
                />
                {filteredFolders.length > 0 && cardData.length > 0 && (
                  <ItemsSectionLabel label="Studies" itemsCollapsed={studiesCollapsed} setItemsCollapsed={setStudiesCollapsed} />
                )}
              </>
            )}
            {!(studiesCollapsed && filteredFolders.length > 0 && !currentFolderId) && (
            <>
            <Row gutter={[16, 16]} className="mb-6">
              {getCampaignsState.apiState === 'loading' ? (
                // Show skeleton loading cards using ANTD Card's built-in loading prop
                Array.from({ length: 6 }).map((_, index) => (
                  <Col
                    span={24}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={8}
                    xxl={6}
                    xxxl={4}
                    key={`skeleton-${index}`}
                  >
                    <CampaignCard loading={true} />
                  </Col>
                ))
              ) : cardData.length > 0 ? (
                cardData.map(item => (
                  <Col
                    span={24}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={8}
                    xxl={6}
                    xxxl={4}
                    key={item.key}
                    draggable={!currentFolderId && filteredFolders.length > 0}
                    onDragStart={!currentFolderId && filteredFolders.length > 0 ? (e) => handleDragStart(e, item) : undefined}
                    onDragEnd={!currentFolderId && filteredFolders.length > 0 ? handleDragEnd : undefined}
                    style={!currentFolderId && filteredFolders.length > 0 ? { cursor: 'grab' } : undefined}
                  >
                    <CampaignCard
                      onClick={() => navigate(getCampaignDetailUrl(item.id))}
                    >
                      <CampaignCardInner>
                        <CampaignCardHeader>
                          <CampaignCardTitle>
                            {item.campaignName}
                          </CampaignCardTitle>
                          <Dropdown
                            trigger={['click']}
                            placement="bottomRight"
                            dropdownRender={() => renderCardMenu(item)}
                          >
                            <CardMenuIcon onClick={e => e.stopPropagation()}>
                              <img
                                src="/images/menu-dots-vertical.svg"
                                alt="Menu"
                              />
                            </CardMenuIcon>
                          </Dropdown>
                        </CampaignCardHeader>
                        <CampaignCardSubtitle className="mb-1">
                          {item.agentName}
                        </CampaignCardSubtitle>
                        <CampaignCardFooter>
                          <StudyStatusTag status={item.status} />
                          <CampaignCardDate>
                            <img
                              src="/images/calendar-icon.svg"
                              alt="Calendar"
                            />
                            <span>Created - {item.createdOn}</span>
                          </CampaignCardDate>
                        </CampaignCardFooter>
                      </CampaignCardInner>
                    </CampaignCard>
                  </Col>
                ))
              ) : (
                <Col span={24} className="text-center">
                  <p>{currentFolderId ? 'No studies in this folder' : 'No studies found'}</p>
                </Col>
              )}
            </Row>
            {totalRecords > 0 && (
              <CardPaginationWrapper>
                <Pagination
                  current={parseInt(filters.page) || 1}
                  pageSize={parseInt(filters.limit) || 12}
                  total={totalRecords}
                  showSizeChanger={true}
                  showTotal={(total, range) =>
                    `${range[0]}-${range[1]} of ${total} studies`
                  }
                  onChange={(page, pageSize) =>
                    handleTableChange({ current: page, pageSize }, null, {})
                  }
                />
              </CardPaginationWrapper>
            )}
            </>
            )}
          </>
        )}
        {activeView === 'table' && (
          <>
            {/* Folder navigation for table view */}
            {!currentFolderId && (
              <>
                <FolderCardsSection
                  filteredFolders={filteredFolders}
                  foldersCollapsed={foldersCollapsed}
                  setFoldersCollapsed={setFoldersCollapsed}
                  onFolderClick={handleFolderClick}
                  onRenameFolderClick={openRenameFolderModal}
                  onDeleteFolderClick={openDeleteFolderModal}
                />
                {filteredFolders.length > 0 && (
                  <ItemsSectionLabel label="Studies" itemsCollapsed={studiesCollapsed} setItemsCollapsed={setStudiesCollapsed} />
                )}
              </>
            )}
            {!(studiesCollapsed && filteredFolders.length > 0 && !currentFolderId) && (
            <ThemeTableCard>
              <Row gutter={[16, 16]}>
                <Col xs={24}>
                  <ThemeTable
                    columns={columns}
                    dataSource={tableData}
                    pagination={{
                      current: parseInt(filters.page) || 1,
                      pageSize: parseInt(filters.limit) || 12,
                      total: totalRecords,
                      showSizeChanger: true,
                      showTotal: (total, range) =>
                        `${range[0]}-${range[1]} of ${total} studies`,
                    }}
                    onChange={handleTableChange}
                    loading={getCampaignsState.apiState === 'loading'}
                    scroll={{ x: 768 }}
                  />
                </Col>
              </Row>
            </ThemeTableCard>
            )}
          </>
        )}
      </ThemeMainWrapper>

      {/* Delete Modal */}
      <ThemeModal
        title="Delete Study"
        open={deleteModalVisible}
        onOk={confirmDelete}
        onCancel={() => {
          setDeleteModalVisible(false);
          setSelectedCampaign(null);
        }}
        confirmLoading={deleteCampaignState.apiState === 'loading'}
        okText="Delete"
        cancelText="Cancel"
        centered
      >
        <p>
          {`Are you sure you want to delete study "${selectedCampaign?.campaignName}"?`}
        </p>
        <p>This action cannot be undone.</p>
      </ThemeModal>

      {/* Folder Modals (Create, Rename, Delete, Move, Remove) */}
      <FolderModals
        createFolderModal={createFolderModal}
        setCreateFolderModal={setCreateFolderModal}
        folderForm={folderForm}
        handleCreateFolder={handleCreateFolder}
        createFolderState={createFolderState}
        renameFolderModal={renameFolderModal}
        setRenameFolderModal={setRenameFolderModal}
        folderToRename={folderToRename}
        renameFolderForm={renameFolderForm}
        handleRenameFolder={handleRenameFolder}
        renameFolderState={renameFolderState}
        deleteFolderModal={deleteFolderModal}
        setDeleteFolderModal={setDeleteFolderModal}
        folderToDelete={folderToDelete}
        handleDeleteFolder={handleDeleteFolder}
        deleteFolderState={deleteFolderState}
        removeFromFolderModal={removeFromFolderModal}
        setRemoveFromFolderModal={setRemoveFromFolderModal}
        itemToRemove={campaignToRemove}
        confirmRemoveFromFolder={confirmRemoveFromFolder}
        removeItemFromFolderState={removeItemFromFolderState}
        moveToFolderModal={moveToFolderModal}
        setMoveToFolderModal={setMoveToFolderModal}
        itemToMove={folderHook.itemToMove}
        selectedFolderId={selectedFolderId}
        setSelectedFolderId={setSelectedFolderId}
        handleMoveToFolder={handleMoveToFolder}
        addItemToFolderState={addItemToFolderState}
        folders={folders}
        currentFolderId={currentFolderId}
        itemLabel="study"
      />
    </>
  );
};

const mapStateToProps = state => ({
  getCampaignsState: state.campaign.getCampaignsState,
  deleteCampaignState: state.campaign.deleteCampaignState,
});

const mapDispatchToProps = dispatch => ({
  getCampaigns: params => dispatch(getCampaignsApi(params)),
  getCampaignsReset: () => dispatch(getCampaignsReset()),
  deleteCampaign: id => dispatch(deleteCampaignApi(id)),
  deleteCampaignReset: () => dispatch(deleteCampaignReset()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Campaigns);
